import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react'
import { AppState, AppAction, Entity, Thread, Question, Answer, QANode } from '@/types'
import { storage } from '@/lib/storage'

function createEmptyState(): AppState {
  return {
    entities: {},
    threads: {},
    qaNodes: {},
    settings: {
      shortcutsEnabled: true,
      verboseMode: true
    }
  }
}

const initialState: AppState = createEmptyState()

// Normalize/upgrade persisted state to ensure required fields exist
function normalizeState(input: any): AppState {
  const safe = input ?? {}
  const entities = typeof safe.entities === 'object' && safe.entities !== null ? safe.entities : {}
  const threads = typeof safe.threads === 'object' && safe.threads !== null ? safe.threads : {}
  const qaNodes = typeof safe.qaNodes === 'object' && safe.qaNodes !== null ? safe.qaNodes : {}
  const settings = typeof safe.settings === 'object' && safe.settings !== null ? safe.settings : {}
  const shortcutsEnabled = typeof settings.shortcutsEnabled === 'boolean' ? settings.shortcutsEnabled : true
  const verboseMode = typeof settings.verboseMode === 'boolean' ? settings.verboseMode : true

  return {
    entities,
    threads,
    qaNodes,
    settings: { shortcutsEnabled, verboseMode }
  }
}

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

function getCurrentTimestamp(): string {
  return new Date().toISOString()
}

function createDemoData(): AppState {
  const now = getCurrentTimestamp()
  
  // Entities
  const fseId = generateId()
  const padesId = generateId()
  const grafId = generateId()

  // Threads (create IDs first)
  const fseT1Id = generateId()
  const fseT2Id = generateId()
  const fseT3Id = generateId()
  const padesT1Id = generateId()
  const padesT2Id = generateId()
  const grafT1Id = generateId()
  const grafT2Id = generateId()

  const fse: Entity = {
    id: fseId,
    name: "FSE",
    createdAt: now,
    updatedAt: now,
    threadIds: [fseT1Id, fseT2Id, fseT3Id],
    isArchived: false,
    archivedAt: null
  }
  const pades: Entity = {
    id: padesId,
    name: "Firma Pades",
    createdAt: now,
    updatedAt: now,
    threadIds: [padesT1Id, padesT2Id],
    isArchived: false,
    archivedAt: null
  }
  const graf: Entity = {
    id: grafId,
    name: "Firma Grafometrica",
    createdAt: now,
    updatedAt: now,
    threadIds: [grafT1Id, grafT2Id],
    isArchived: false,
    archivedAt: null
  }

  // Thread objects (rootQuestionId to be set below)
  const fseT1: Thread = { id: fseT1Id, entityId: fseId, title: "API Paziente - Codice fiscale", rootQuestionId: '', createdAt: now, updatedAt: now }
  const fseT2: Thread = { id: fseT2Id, entityId: fseId, title: "API Documento - Campo timestamp", rootQuestionId: '', createdAt: now, updatedAt: now }
  const fseT3: Thread = { id: fseT3Id, entityId: fseId, title: "API Referto - Identificativo univoco", rootQuestionId: '', createdAt: now, updatedAt: now }
  const padesT1: Thread = { id: padesT1Id, entityId: padesId, title: "Firma PDF - Algoritmo di digest", rootQuestionId: '', createdAt: now, updatedAt: now }
  const padesT2: Thread = { id: padesT2Id, entityId: padesId, title: "Firma PDF - Campi Reason/Location", rootQuestionId: '', createdAt: now, updatedAt: now }
  const grafT1: Thread = { id: grafT1Id, entityId: grafId, title: "Dati biometrici - Curva di pressione", rootQuestionId: '', createdAt: now, updatedAt: now }
  const grafT2: Thread = { id: grafT2Id, entityId: grafId, title: "Metadati dispositivo - FirmwareVersion obbligatorio?", rootQuestionId: '', createdAt: now, updatedAt: now }

  // Q&A nodes
  // FSE T1: conclusive answer + consequences
  const fseQ1Id = generateId()
  const fseA1Id = generateId()
  const fseQ1: Question = { id: fseQ1Id, type: 'question', text: 'Qual è il formato e la capitalizzazione attesi per codiceFiscale?', createdAt: now, updatedAt: now, answerId: fseA1Id }
  const fseA1: Answer = {
    id: fseA1Id,
    type: 'answer',
    text: 'Usare un CF alfanumerico di 16 caratteri in maiuscolo; validare il checksum; rifiutare il minuscolo.',
    createdAt: now,
    updatedAt: now,
    status: 'conclusive',
    followUpQuestionIds: [],
    consequences: [
      { id: generateId(), title: 'Aggiungere validatore CF lato client', createdAt: now },
      { id: generateId(), title: 'Restituire 422 per formato CF non valido', createdAt: now }
    ]
  }

  // FSE T2: inconclusive answer + follow-up open
  const fseQ2Id = generateId()
  const fseA2Id = generateId()
  const fseQ3Id = generateId()
  const fseQ2: Question = { id: fseQ2Id, type: 'question', text: 'createdAt deve essere in ISO 8601 con offset di fuso orario?', createdAt: now, updatedAt: now, answerId: fseA2Id }
  const fseA2: Answer = { id: fseA2Id, type: 'answer', text: 'Probabilmente ISO 8601 con Z o +hh:mm, serve conferma.', createdAt: now, updatedAt: now, status: 'inconclusive', followUpQuestionIds: [fseQ3Id], consequences: [] }
  const fseQ3: Question = { id: fseQ3Id, type: 'question', text: 'Accettiamo i millisecondi in createdAt?', createdAt: now, updatedAt: now, answerId: undefined }

  // FSE T3: open question (no answer)
  const fseQ4Id = generateId()
  const fseQ4: Question = { id: fseQ4Id, type: 'question', text: "'idReferto' è un UUID v4 o specifico del provider?", createdAt: now, updatedAt: now, answerId: undefined }

  // PADES T1: conclusive answer + consequence
  const padesQ1Id = generateId()
  const padesA1Id = generateId()
  const padesQ1: Question = { id: padesQ1Id, type: 'question', text: 'Quale algoritmo di digest deve essere usato per le firme PDF?', createdAt: now, updatedAt: now, answerId: padesA1Id }
  const padesA1: Answer = { id: padesA1Id, type: 'answer', text: 'Imporre SHA-256 per CMS/PKCS#7; deprecare SHA-1.', createdAt: now, updatedAt: now, status: 'conclusive', followUpQuestionIds: [], consequences: [{ id: generateId(), title: 'Bloccare firme SHA-1 in verifica', createdAt: now }] }

  // PADES T2: inconclusive answer + follow-up
  const padesQ2Id = generateId()
  const padesA2Id = generateId()
  const padesQ3Id = generateId()
  const padesQ2: Question = { id: padesQ2Id, type: 'question', text: 'I campi Reason e Location sono obbligatori nel dizionario della firma?', createdAt: now, updatedAt: now, answerId: padesA2Id }
  const padesA2: Answer = { id: padesA2Id, type: 'answer', text: 'Non strettamente obbligatori, ma raccomandati. Serve conferma di policy.', createdAt: now, updatedAt: now, status: 'inconclusive', followUpQuestionIds: [padesQ3Id], consequences: [] }
  const padesQ3: Question = { id: padesQ3Id, type: 'question', text: 'Se presenti, i campi devono essere localizzati (it-IT) o in inglese?', createdAt: now, updatedAt: now, answerId: undefined }

  // GRAF T1: inconclusive answer, with consequence
  const grafQ1Id = generateId()
  const grafA1Id = generateId()
  const grafQ1: Question = { id: grafQ1Id, type: 'question', text: 'Come serializziamo i campioni della curva di pressione (Hz, precisione)?', createdAt: now, updatedAt: now, answerId: grafA1Id }
  const grafA1: Answer = { id: grafA1Id, type: 'answer', text: 'Proposta: 200Hz, 2 decimali; confermare con i vendor dei dispositivi.', createdAt: now, updatedAt: now, status: 'inconclusive', followUpQuestionIds: [], consequences: [{ id: generateId(), title: 'Pianificare confronto con i vendor sui ratei di campionamento', createdAt: now }] }

  // GRAF T2: conclusive answer
  const grafQ2Id = generateId()
  const grafA2Id = generateId()
  const grafQ2: Question = { id: grafQ2Id, type: 'question', text: 'FirmwareVersion è obbligatorio nei metadati del dispositivo?', createdAt: now, updatedAt: now, answerId: grafA2Id }
  const grafA2: Answer = { id: grafA2Id, type: 'answer', text: 'Sì, versione semantica (MAJOR.MINOR.PATCH).', createdAt: now, updatedAt: now, status: 'conclusive', followUpQuestionIds: [], consequences: [] }

  // Set root question ids
  fseT1.rootQuestionId = fseQ1Id
  fseT2.rootQuestionId = fseQ2Id
  fseT3.rootQuestionId = fseQ4Id
  padesT1.rootQuestionId = padesQ1Id
  padesT2.rootQuestionId = padesQ2Id
  grafT1.rootQuestionId = grafQ1Id
  grafT2.rootQuestionId = grafQ2Id

  return {
    entities: {
      [fseId]: fse,
      [padesId]: pades,
      [grafId]: graf,
    },
    threads: {
      [fseT1Id]: fseT1,
      [fseT2Id]: fseT2,
      [fseT3Id]: fseT3,
      [padesT1Id]: padesT1,
      [padesT2Id]: padesT2,
      [grafT1Id]: grafT1,
      [grafT2Id]: grafT2,
    },
    qaNodes: {
      [fseQ1Id]: fseQ1,
      [fseA1Id]: fseA1,
      [fseQ2Id]: fseQ2,
      [fseA2Id]: fseA2,
      [fseQ3Id]: fseQ3,
      [fseQ4Id]: fseQ4,
      [padesQ1Id]: padesQ1,
      [padesA1Id]: padesA1,
      [padesQ2Id]: padesQ2,
      [padesA2Id]: padesA2,
      [padesQ3Id]: padesQ3,
      [grafQ1Id]: grafQ1,
      [grafA1Id]: grafA1,
      [grafQ2Id]: grafQ2,
      [grafA2Id]: grafA2,
    },
    settings: {
      shortcutsEnabled: true,
      verboseMode: true
    }
  }
}

function appReducer(state: AppState, action: AppAction): AppState {
  const now = getCurrentTimestamp()
  
  switch (action.type) {
    case 'RESET_DATA':
      return createDemoData()

    case 'CLEAR_ALL_DATA':
      return createEmptyState()
      
    case 'SET_STATE':
      return normalizeState(action.payload)
      
    case 'CREATE_ENTITY': {
      const id = generateId()
      const entity: Entity = {
        id,
        name: action.payload.name,
        createdAt: now,
        updatedAt: now,
        threadIds: [],
        isArchived: false,
        archivedAt: null
      }
      return {
        ...state,
        entities: {
          ...state.entities,
          [id]: entity
        }
      }
    }
    
    case 'UPDATE_ENTITY': {
      const entity = state.entities[action.payload.id]
      if (!entity) return state
      
      return {
        ...state,
        entities: {
          ...state.entities,
          [action.payload.id]: {
            ...entity,
            name: action.payload.name,
            updatedAt: now
          }
        }
      }
    }
    
    case 'DELETE_ENTITY': {
      const entity = state.entities[action.payload.id]
      if (!entity) return state
      
      const { [action.payload.id]: deletedEntity, ...remainingEntities } = state.entities
      
      // Delete all threads and Q&A nodes for this entity
      const threadsToDelete = entity.threadIds
      const { threads: updatedThreads, qaNodes: updatedQANodes } = threadsToDelete.reduce(
        (acc, threadId) => {
          const thread = state.threads[threadId]
          if (thread) {
            const { [threadId]: deletedThread, ...remainingThreads } = acc.threads
            const qaNodesInThread = getAllQANodesInThread(thread.rootQuestionId, state.qaNodes)
            const updatedQANodes = { ...acc.qaNodes }
            qaNodesInThread.forEach(nodeId => {
              delete updatedQANodes[nodeId]
            })
            return {
              threads: remainingThreads,
              qaNodes: updatedQANodes
            }
          }
          return acc
        },
        { threads: { ...state.threads }, qaNodes: { ...state.qaNodes } }
      )
      
      return {
        ...state,
        entities: remainingEntities,
        threads: updatedThreads,
        qaNodes: updatedQANodes
      }
    }
    
    case 'ARCHIVE_ENTITY': {
      const entity = state.entities[action.payload.id]
      if (!entity) return state
      
      return {
        ...state,
        entities: {
          ...state.entities,
          [action.payload.id]: {
            ...entity,
            isArchived: true,
            archivedAt: now,
            updatedAt: now
          }
        }
      }
    }
    
    case 'UNARCHIVE_ENTITY': {
      const entity = state.entities[action.payload.id]
      if (!entity) return state
      
      return {
        ...state,
        entities: {
          ...state.entities,
          [action.payload.id]: {
            ...entity,
            isArchived: false,
            archivedAt: null,
            updatedAt: now
          }
        }
      }
    }
    
    case 'IMPORT_ENTITY': {
      const payload = action.payload ?? {}
      const rawEntity = payload.entity ?? payload
      if (!rawEntity || typeof rawEntity !== 'object') return state

      const { threads: _legacyThreads, qaNodes: _legacyQANodes, ...entityRest } = rawEntity as Record<string, any>
      const entityName = typeof entityRest.name === 'string' && entityRest.name.trim().length > 0
        ? entityRest.name
        : 'Imported entity'

      const newEntityId = generateId()
      const newEntity: Entity = {
        ...(entityRest as Partial<Entity>),
        id: newEntityId,
        name: entityName,
        threadIds: [],
        createdAt: typeof entityRest.createdAt === 'string' ? entityRest.createdAt : now,
        updatedAt: now,
        isArchived: Boolean(entityRest.isArchived),
        archivedAt: Boolean(entityRest.isArchived) ? entityRest.archivedAt ?? null : null
      }

      const rawThreads = Array.isArray(payload.threads) ? payload.threads : []
      const rawQANodes = payload.qaNodes && typeof payload.qaNodes === 'object' ? payload.qaNodes : {}

      const qaNodeIdMapping: Record<string, string> = {}
      Object.keys(rawQANodes).forEach(oldId => {
        qaNodeIdMapping[oldId] = generateId()
      })

      const newThreads: Record<string, Thread> = {}
      const newThreadIds: string[] = []

      rawThreads.forEach((thread: any) => {
        if (!thread || typeof thread !== 'object') return
        const { qaNodes: _threadQANodes, ...threadRest } = thread
        const originalRootQuestionId = threadRest.rootQuestionId
        if (originalRootQuestionId && !qaNodeIdMapping[originalRootQuestionId]) {
          qaNodeIdMapping[originalRootQuestionId] = generateId()
        }

        const newThreadId = generateId()
        const mappedRootId = originalRootQuestionId ? qaNodeIdMapping[originalRootQuestionId] : generateId()

        newThreads[newThreadId] = {
          ...(threadRest as Partial<Thread>),
          id: newThreadId,
          entityId: newEntityId,
          title: typeof threadRest.title === 'string' ? threadRest.title : 'Imported thread',
          rootQuestionId: mappedRootId,
          createdAt: typeof threadRest.createdAt === 'string' ? threadRest.createdAt : now,
          updatedAt: now
        }
        newThreadIds.push(newThreadId)
      })

      newEntity.threadIds = newThreadIds
      if (!newEntity.isArchived) {
        newEntity.archivedAt = null
      }

      const newQANodes: Record<string, QANode> = {}
      Object.values(rawQANodes).forEach((qaNode: any) => {
        if (!qaNode || typeof qaNode !== 'object') return
        const newQANodeId = qaNodeIdMapping[qaNode.id]
        if (!newQANodeId) return

        const baseNode: any = {
          ...qaNode,
          id: newQANodeId,
          createdAt: typeof qaNode.createdAt === 'string' ? qaNode.createdAt : now,
          updatedAt: now
        }

        if (qaNode.type === 'question') {
          baseNode.answerId = qaNode.answerId ? qaNodeIdMapping[qaNode.answerId] : undefined
        } else if (qaNode.type === 'answer') {
          baseNode.followUpQuestionIds = Array.isArray(qaNode.followUpQuestionIds)
            ? qaNode.followUpQuestionIds.map((id: string) => qaNodeIdMapping[id]).filter(Boolean)
            : []
          baseNode.consequences = Array.isArray(qaNode.consequences)
            ? qaNode.consequences.map((consequence: any) => ({
                ...consequence,
                id: generateId(),
                createdAt: typeof consequence?.createdAt === 'string' ? consequence.createdAt : now,
                completedAt: consequence?.completedAt ?? undefined
              }))
            : []
        }

        newQANodes[newQANodeId] = baseNode
      })

      return {
        ...state,
        entities: {
          ...state.entities,
          [newEntity.id]: newEntity
        },
        threads: {
          ...state.threads,
          ...newThreads
        },
        qaNodes: {
          ...state.qaNodes,
          ...newQANodes
        }
      }
    }
    
    case 'CREATE_THREAD': {
      const id = generateId()
      const rootQuestionId = generateId()
      
      const thread: Thread = {
        id,
        entityId: action.payload.entityId,
        title: action.payload.title,
        rootQuestionId,
        createdAt: now,
        updatedAt: now
      }
      
      const rootQuestion: Question = {
        id: rootQuestionId,
        type: "question",
        text: "",
        createdAt: now,
        updatedAt: now,
        answerId: undefined
      }
      
      const entity = state.entities[action.payload.entityId]
      if (!entity) return state
      
      return {
        ...state,
        entities: {
          ...state.entities,
          [action.payload.entityId]: {
            ...entity,
            threadIds: [...entity.threadIds, id],
            updatedAt: now
          }
        },
        threads: {
          ...state.threads,
          [id]: thread
        },
        qaNodes: {
          ...state.qaNodes,
          [rootQuestionId]: rootQuestion
        }
      }
    }
    
    case 'UPDATE_THREAD': {
      const thread = state.threads[action.payload.id]
      if (!thread) return state
      
      return {
        ...state,
        threads: {
          ...state.threads,
          [action.payload.id]: {
            ...thread,
            title: action.payload.title,
            updatedAt: now
          }
        }
      }
    }
    
    case 'DELETE_THREAD': {
      const thread = state.threads[action.payload.id]
      if (!thread) return state
      
      const { [action.payload.id]: deletedThread, ...remainingThreads } = state.threads
      
      // Remove thread from entity
      const entity = state.entities[thread.entityId]
      const updatedEntity = {
        ...entity,
        threadIds: entity.threadIds.filter(id => id !== action.payload.id),
        updatedAt: now
      }
      
      // Delete all Q&A nodes in this thread
      const qaNodesInThread = getAllQANodesInThread(thread.rootQuestionId, state.qaNodes)
      const updatedQANodes = { ...state.qaNodes }
      qaNodesInThread.forEach(nodeId => {
        delete updatedQANodes[nodeId]
      })
      
      return {
        ...state,
        entities: {
          ...state.entities,
          [thread.entityId]: updatedEntity
        },
        threads: remainingThreads,
        qaNodes: updatedQANodes
      }
    }
    
    case 'IMPORT_THREAD': {
      const { thread: importedThread, entityId, qaNodes: importedQANodes } = action.payload ?? {}
      if (!importedThread || !entityId) return state

      const entity = state.entities[entityId]
      if (!entity) return state

      const { qaNodes: _threadQANodes, ...threadRest } = importedThread as Record<string, any>
      const rawQANodes = importedQANodes && typeof importedQANodes === 'object' ? importedQANodes : {}

      const qaNodeIdMapping: Record<string, string> = {}
      Object.keys(rawQANodes).forEach(oldId => {
        qaNodeIdMapping[oldId] = generateId()
      })

      if (threadRest.rootQuestionId && !qaNodeIdMapping[threadRest.rootQuestionId]) {
        qaNodeIdMapping[threadRest.rootQuestionId] = generateId()
      }

      const newThreadId = generateId()
      const mappedRootId = threadRest.rootQuestionId ? qaNodeIdMapping[threadRest.rootQuestionId] : generateId()

      const newThread: Thread = {
        ...(threadRest as Partial<Thread>),
        id: newThreadId,
        entityId,
        title: typeof threadRest.title === 'string' ? threadRest.title : 'Imported thread',
        rootQuestionId: mappedRootId,
        createdAt: typeof threadRest.createdAt === 'string' ? threadRest.createdAt : now,
        updatedAt: now
      }

      const newQANodes: Record<string, QANode> = {}
      Object.values(rawQANodes).forEach((qaNode: any) => {
        if (!qaNode || typeof qaNode !== 'object') return
        const newQANodeId = qaNodeIdMapping[qaNode.id]
        if (!newQANodeId) return

        const baseNode: any = {
          ...qaNode,
          id: newQANodeId,
          createdAt: typeof qaNode.createdAt === 'string' ? qaNode.createdAt : now,
          updatedAt: now
        }

        if (qaNode.type === 'question') {
          baseNode.answerId = qaNode.answerId ? qaNodeIdMapping[qaNode.answerId] : undefined
        } else if (qaNode.type === 'answer') {
          baseNode.followUpQuestionIds = Array.isArray(qaNode.followUpQuestionIds)
            ? qaNode.followUpQuestionIds.map((id: string) => qaNodeIdMapping[id]).filter(Boolean)
            : []
          baseNode.consequences = Array.isArray(qaNode.consequences)
            ? qaNode.consequences.map((consequence: any) => ({
                ...consequence,
                id: generateId(),
                createdAt: typeof consequence?.createdAt === 'string' ? consequence.createdAt : now,
                completedAt: consequence?.completedAt ?? undefined
              }))
            : []
        }

        newQANodes[newQANodeId] = baseNode
      })

      return {
        ...state,
        entities: {
          ...state.entities,
          [entityId]: {
            ...entity,
            threadIds: [...entity.threadIds, newThreadId],
            updatedAt: now
          }
        },
        threads: {
          ...state.threads,
          [newThreadId]: newThread
        },
        qaNodes: {
          ...state.qaNodes,
          ...newQANodes
        }
      }
    }
    
    case 'CREATE_QUESTION': {
      const id = generateId()
      const question: Question = {
        id,
        type: "question",
        text: action.payload.text,
        createdAt: now,
        updatedAt: now,
        answerId: undefined
      }
      
      let updatedState = {
        ...state,
        qaNodes: {
          ...state.qaNodes,
          [id]: question
        }
      }
      
      // If this is a follow-up question, add it to the parent answer
      if (action.payload.parentAnswerId) {
        const parentAnswer = state.qaNodes[action.payload.parentAnswerId] as Answer
        if (parentAnswer && parentAnswer.type === 'answer') {
          updatedState.qaNodes[action.payload.parentAnswerId] = {
            ...parentAnswer,
            followUpQuestionIds: [...parentAnswer.followUpQuestionIds, id]
          }
        }
      }
      
      return updatedState
    }
    
    case 'UPDATE_QUESTION': {
      const question = state.qaNodes[action.payload.id] as Question
      if (!question || question.type !== 'question') return state
      
      return {
        ...state,
        qaNodes: {
          ...state.qaNodes,
          [action.payload.id]: {
            ...question,
            text: action.payload.text,
            updatedAt: now
          }
        }
      }
    }
    
    case 'DELETE_QUESTION': {
      const question = state.qaNodes[action.payload.id] as Question
      if (!question || question.type !== 'question') return state
      
      // Delete the question and all its descendants
      const nodesToDelete = getAllDescendantNodes(action.payload.id, state.qaNodes)
      const updatedQANodes = { ...state.qaNodes }
      nodesToDelete.forEach(nodeId => {
        delete updatedQANodes[nodeId]
      })
      
      // Remove question from parent answer's followUpQuestionIds
      const parentAnswer = Object.values(state.qaNodes).find(node => 
        node.type === 'answer' && (node as Answer).followUpQuestionIds.includes(action.payload.id)
      ) as Answer
      
      if (parentAnswer) {
        updatedQANodes[parentAnswer.id] = {
          ...parentAnswer,
          followUpQuestionIds: parentAnswer.followUpQuestionIds.filter(id => id !== action.payload.id)
        }
      }
      
      return {
        ...state,
        qaNodes: updatedQANodes
      }
    }
    
    case 'CREATE_ANSWER': {
      const id = generateId()
      const answer: Answer = {
        id,
        type: "answer",
        text: action.payload.text,
        createdAt: now,
        updatedAt: now,
        status: action.payload.status,
        followUpQuestionIds: [],
        consequences: []
      }
      
      const question = state.qaNodes[action.payload.questionId] as Question
      if (!question || question.type !== 'question') return state
      
      return {
        ...state,
        qaNodes: {
          ...state.qaNodes,
          [id]: answer,
          [action.payload.questionId]: {
            ...question,
            answerId: id
          }
        }
      }
    }
    
    case 'UPDATE_ANSWER': {
      const answer = state.qaNodes[action.payload.id] as Answer
      if (!answer || answer.type !== 'answer') return state
      
      return {
        ...state,
        qaNodes: {
          ...state.qaNodes,
          [action.payload.id]: {
            ...answer,
            ...(action.payload.text !== undefined && { text: action.payload.text }),
            ...(action.payload.status !== undefined && { status: action.payload.status }),
            updatedAt: now
          }
        }
      }
    }
    
    case 'DELETE_ANSWER': {
      const answer = state.qaNodes[action.payload.id] as Answer
      if (!answer || answer.type !== 'answer') return state
      
      // Delete the answer and all its descendants
      const nodesToDelete = getAllDescendantNodes(action.payload.id, state.qaNodes)
      const updatedQANodes = { ...state.qaNodes }
      nodesToDelete.forEach(nodeId => {
        delete updatedQANodes[nodeId]
      })
      
      // Remove answer from parent question's answerId
      const parentQuestion = Object.values(state.qaNodes).find(node => 
        node.type === 'question' && (node as Question).answerId === action.payload.id
      ) as Question
      
      if (parentQuestion) {
        updatedQANodes[parentQuestion.id] = {
          ...parentQuestion,
          answerId: undefined
        }
      }
      
      return {
        ...state,
        qaNodes: updatedQANodes
      }
    }
    
    case 'ADD_CONSEQUENCE': {
      const answer = state.qaNodes[action.payload.answerId] as Answer
      if (!answer || answer.type !== 'answer') return state
      
      const consequence = {
        id: action.payload.consequenceId || generateId(),
        title: action.payload.title,
        createdAt: now
      }
      
      return {
        ...state,
        qaNodes: {
          ...state.qaNodes,
          [action.payload.answerId]: {
            ...answer,
            consequences: [...answer.consequences, consequence]
          }
        }
      }
    }
    
    case 'UPDATE_CONSEQUENCE': {
      const answer = state.qaNodes[action.payload.answerId] as Answer
      if (!answer || answer.type !== 'answer') return state
      
      const updatedConsequences = answer.consequences.map(c => {
        if (c.id === action.payload.consequenceId) {
          return {
            ...c,
            ...(action.payload.title !== undefined && { title: action.payload.title }),
            ...(action.payload.notes !== undefined && { notes: action.payload.notes }),
            ...(action.payload.completed !== undefined && { 
              completedAt: action.payload.completed ? now : undefined 
            })
          }
        }
        return c
      })
      
      return {
        ...state,
        qaNodes: {
          ...state.qaNodes,
          [action.payload.answerId]: {
            ...answer,
            consequences: updatedConsequences
          }
        }
      }
    }
    
    case 'DELETE_CONSEQUENCE': {
      const answer = state.qaNodes[action.payload.answerId] as Answer
      if (!answer || answer.type !== 'answer') return state
      
      return {
        ...state,
        qaNodes: {
          ...state.qaNodes,
          [action.payload.answerId]: {
            ...answer,
            consequences: answer.consequences.filter(c => c.id !== action.payload.consequenceId)
          }
        }
      }
    }
    
    case 'TOGGLE_SHORTCUTS': {
      return {
        ...state,
        settings: {
          ...state.settings,
          shortcutsEnabled: action.payload.enabled
        }
      }
    }

    case 'TOGGLE_VERBOSE_MODE': {
      return {
        ...state,
        settings: {
          ...state.settings,
          verboseMode: action.payload.enabled
        }
      }
    }
    
    default:
      return state
  }
}

// Helper functions
function getAllQANodesInThread(rootQuestionId: string, qaNodes: Record<string, QANode>): string[] {
  const result: string[] = []
  const visited = new Set<string>()
  
  function traverse(nodeId: string) {
    if (visited.has(nodeId) || !qaNodes[nodeId]) return
    
    visited.add(nodeId)
    result.push(nodeId)
    
    const node = qaNodes[nodeId]
    if (node.type === 'question') {
      if (node.answerId) {
        traverse(node.answerId)
      }
    } else {
      node.followUpQuestionIds.forEach(traverse)
    }
  }
  
  traverse(rootQuestionId)
  return result
}

function getAllDescendantNodes(nodeId: string, qaNodes: Record<string, QANode>): string[] {
  const result: string[] = []
  const visited = new Set<string>()
  
  function traverse(id: string) {
    if (visited.has(id) || !qaNodes[id]) return
    
    visited.add(id)
    result.push(id)
    
    const node = qaNodes[id]
    if (node.type === 'question') {
      if (node.answerId) {
        traverse(node.answerId)
      }
    } else {
      node.followUpQuestionIds.forEach(traverse)
    }
  }
  
  traverse(nodeId)
  return result
}

export function isQuestionOpen(questionId: string, qaNodes: Record<string, QANode>): boolean {
  const question = qaNodes[questionId] as Question
  if (!question || question.type !== 'question') return false
  // A question is open as long as it has no answer
  return !question.answerId
}

export function getOpenQuestionCount(entityId: string, state: AppState): number {
  const entity = state.entities[entityId]
  if (!entity) return 0
  
  return entity.threadIds.reduce((count, threadId) => {
    const thread = state.threads[threadId]
    if (!thread) return count
    
    const threadOpenCount = getOpenQuestionCountInThread(thread.rootQuestionId, state.qaNodes)
    return count + threadOpenCount
  }, 0)
}

export function getOpenQuestionCountInThread(rootQuestionId: string, qaNodes: Record<string, QANode>): number {
  const allQuestions = getAllQANodesInThread(rootQuestionId, qaNodes)
    .filter(nodeId => qaNodes[nodeId]?.type === 'question')
  
  return allQuestions.filter(questionId => isQuestionOpen(questionId, qaNodes)).length
}

// Thread open status: open if the last question is open (no answer)
// or if the last answer (to that last question) is not conclusive.
export function isThreadOpen(rootQuestionId: string, qaNodes: Record<string, QANode>): boolean {
  const nodes = getAllQANodesInThread(rootQuestionId, qaNodes)
  const lastQuestionId = [...nodes].reverse().find(id => qaNodes[id]?.type === 'question') as string | undefined
  if (!lastQuestionId) return false
  const question = qaNodes[lastQuestionId] as Question
  if (!question.answerId) return true
  const answer = qaNodes[question.answerId] as Answer | undefined
  if (!answer || answer.type !== 'answer') return true
  return answer.status !== 'conclusive'
}

const AppContext = createContext<{
  state: AppState
  dispatch: React.Dispatch<AppAction>
  isReady: boolean
} | null>(null)

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState)
  const [isReady, setIsReady] = React.useState(false)

  // Load data from storage on mount
  useEffect(() => {
    let cancelled = false
    async function loadData() {
      try {
        const savedState = await storage.get<AppState>('app-state')
        if (savedState && (Object.keys(savedState.entities || {}).length > 0 || Object.keys(savedState.threads || {}).length > 0)) {
          // Normalize saved data to ensure all required fields exist
          const normalized = normalizeState(savedState)
          if (!cancelled) {
            dispatch({ type: 'SET_STATE', payload: normalized } as any)
          }
        }
      } finally {
        if (!cancelled) setIsReady(true)
      }
    }

    loadData()
    return () => { cancelled = true }
  }, [])

  // Save data to storage whenever state changes (only after initial load completes)
  useEffect(() => {
    if (!isReady) return
    storage.set('app-state', state)
  }, [state, isReady])

  return (
    <AppContext.Provider value={{ state, dispatch, isReady }}>
      {children}
    </AppContext.Provider>
  )
}

export function useApp() {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}